import type { EventListItem, EventUpdateInput } from "@cdt/shared";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";
import { useUpdateEvent } from "@/hooks/useEventActions";

/** Convert an ISO date string to the "YYYY-MM-DDTHH:mm" format needed by
 *  datetime-local inputs. Dates are displayed in UTC to stay consistent with
 *  how the API stores them. */
export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 16);
}

/** Convert the datetime-local input value back to a full ISO string (UTC). */
export function datetimeLocalToIso(local: string): string | null {
  if (!local) return null;
  return `${local}:00.000Z`;
}

export interface EditEventDialogProps {
  event: EventListItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditEventDialog({
  event,
  open,
  onOpenChange,
}: EditEventDialogProps) {
  const categories = useCategoriesQuery();
  const updateEvent = useUpdateEvent();

  const [title, setTitle] = useState(event.title ?? "");
  const [description, setDescription] = useState(event.description ?? "");
  const [startDate, setStartDate] = useState(
    isoToDatetimeLocal(event.startDate),
  );
  const [endDate, setEndDate] = useState(isoToDatetimeLocal(event.endDate));
  const [location, setLocation] = useState(event.location ?? "");
  const [organizer, setOrganizer] = useState(event.organizer ?? "");
  const [eventType, setEventType] = useState(event.eventType ?? "");
  const [categoryId, setCategoryId] = useState(event.categoryId ?? "");
  const [status, setStatus] = useState<"MANUAL_REVIEW" | "CREATED" | "IGNORED">(
    event.status === "PENDING" || event.status === "ERROR"
      ? "MANUAL_REVIEW"
      : (event.status as "MANUAL_REVIEW" | "CREATED" | "IGNORED"),
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const patch: EventUpdateInput = {
      title: title.trim() || undefined,
      description: description.trim() || null,
      startDate: datetimeLocalToIso(startDate),
      endDate: datetimeLocalToIso(endDate),
      location: location.trim() || null,
      organizer: organizer.trim() || null,
      eventType: eventType.trim() || null,
      categoryId: categoryId || null,
      status,
    };

    updateEvent.mutate(
      { id: event.id, patch },
      {
        onSuccess: (data) => {
          onOpenChange(false);
          if (data.status === "ERROR") {
            toast.warning(
              `Modifiche salvate ma sync Outlook fallito: ${data.errorMessage ?? "errore sconosciuto"}`,
            );
          }
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifica evento</DialogTitle>
          <DialogDescription>
            I cambiamenti vengono sincronizzati con il calendario Outlook.
          </DialogDescription>
        </DialogHeader>

        <form id="edit-event-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="edit-title">Titolo</Label>
              <Input
                id="edit-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Titolo evento"
              />
            </div>

            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="edit-description">Descrizione</Label>
              <Textarea
                id="edit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Descrizione opzionale"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-startDate">Data inizio</Label>
              <Input
                id="edit-startDate"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-endDate">Data fine</Label>
              <Input
                id="edit-endDate"
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-location">Luogo</Label>
              <Input
                id="edit-location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Luogo opzionale"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-organizer">Organizzatore</Label>
              <Input
                id="edit-organizer"
                value={organizer}
                onChange={(e) => setOrganizer(e.target.value)}
                placeholder="Organizzatore opzionale"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-eventType">Tipo evento</Label>
              <Input
                id="edit-eventType"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                placeholder="Tipo opzionale"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-category">Categoria</Label>
              <select
                id="edit-category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="">Nessuna categoria</option>
                {categories.data?.items.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="edit-status">Stato</Label>
              <select
                id="edit-status"
                value={status}
                onChange={(e) =>
                  setStatus(
                    e.target.value as "MANUAL_REVIEW" | "CREATED" | "IGNORED",
                  )
                }
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                <option value="MANUAL_REVIEW">In revisione</option>
                <option value="CREATED">Creato nel calendario</option>
                <option value="IGNORED">Ignorato</option>
              </select>
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateEvent.isPending}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            form="edit-event-form"
            disabled={updateEvent.isPending}
          >
            {updateEvent.isPending ? "Salvataggio..." : "Salva modifiche"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
